import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

class GrouponForm extends Component {
  #error = false;
  #loading = false;

  #form = this.querySelector('form');
  #link = this.#form?.querySelector('#link');
  #input = this.#form?.querySelector('input');
  #label = this.#form?.querySelector('#label');
  #apply = this.#form?.querySelector('a:not(#link)');
  #controls = this.#form?.querySelector('#controls');

  #messages = {
    default: '',
    error: 'Please enter a valid discount code.',
    discountNotFound: 'Discount code not found. Please check the code and try again.',
    discountUnavailable: 'This discount is unavailable. It may have already been used.',
  };

  #productIdMap = {
    6849179910218: {
      url: '/products/bulk-photo-scanning',
      name: '4x6 Photo Legacybox - Bulk Photo Scanning',
    },
    4506041909322: {
      url: '/products/legacybox-digitizing-kit',
      name: 'Legacybox Digitizing Kit',
    },
    6659088023626: {
      url: '/products/legacybox-digitizing-kit-9-tapes-film',
      name: '$9 Tape & Film Transfers',
    },
  };

  connectedCallback() {
    super.connectedCallback();

    this.#messages.default = this.#label?.getAttribute('data-initial') || '';

    this.#attachEventListeners();
  }

  #attachEventListeners() {
    this.#apply?.addEventListener('click', this.#handleClick.bind(this));
    this.#input?.addEventListener('input', this.#isCodeValid.bind(this));
    this.#form?.addEventListener('submit', this.#handleSubmit.bind(this));
  }

  /**
   *
   * @param {Event} e
   * @returns void
   */
  #handleClick(e) {
    e.preventDefault();

    this.#handleSubmit(e);
  }

  /**
   *
   * @param {Event} e
   * @returns void
   */
  async #handleSubmit(e) {
    e.preventDefault();

    if (this.#error || this.#loading) return;

    this.#loading = true;

    try {
      const code = this.#getCode();

      if (!this.#isCodeValid()) return;

      const data = await this.#getDiscountData(code);

      if (!data || data.status === 'fail') {
        this.#setLabel(this.#messages.discountNotFound);
        return;
      }

      const hasUsageLimit =
        typeof data.asyncUsageCount === 'number' && typeof data.usageLimit === 'number';

      if (hasUsageLimit && data.asyncUsageCount >= data.usageLimit) {
        this.#setLabel(this.#messages.discountUnavailable);
        return;
      }

      this.#applyDiscount(data, code);
    } catch (err) {
      console.error(err);
      this.#loading = false;
    } finally {
      this.#loading = false;
    }
  }

  #getCode() {
    return this.#input?.value?.trim().toUpperCase() || '';
  }

  #isCodeValid() {
    const code = this.#getCode();

    if (!code.length) {
      this.#error = true;
      this.#setLabel(this.#messages.default);

      return false;
    }

    if (code.length >= 6 && code.startsWith('GB')) {
      this.#error = false;
      this.#setLabel(this.#messages.default);
      return true;
    }

    this.#error = true;
    this.#setLabel(this.#messages.error);
    return false;
  }

  /**
   *
   * @param {String} message
   */
  #setLabel(message) {
    if (this.#label) {
      this.#label.innerText = message;
    }
  }

  /**
   *
   * @param {String} code
   */
  async #getDiscountData(code) {
    try {
      const { data } = await fetch(Theme.legacyBox.discounts.worker, {
        method: 'POST',
        body: JSON.stringify({ code }),
      }).then((res) => res.json());

      return data;
    } catch (error) {
      console.error(error);

      return null;
    }
  }

  /**
   *
   * @param {*} data
   * @param {String} code
   */
  #applyDiscount(data, code) {
    let productName = 'Legacybox'; // Default name
    let productUrl = '/products/legacybox-digitizing-kit'; // Default url

    const valueData = data.customerGets?.value;
    const isPercent = Boolean(valueData.percentage);
    const fixedAmount = Number(valueData.amount?.amount);
    const fixedAmountCents = Number.isFinite(fixedAmount) ? Math.round(fixedAmount * 100) : 0;
    const discountValue = isPercent
      ? `${valueData.percentage * 100}%`
      : formatMoney(fixedAmountCents, Theme.format, Shopify.currency.active);

    if (
      data.customerGets?.items?.products?.nodes &&
      data.customerGets?.items?.products?.nodes.length > 0
    ) {
      // Check each entitled product ID against our mapping
      for (let i = 0; i < data.customerGets?.items?.products?.nodes.length; i++) {
        const item = data.customerGets?.items?.products?.nodes[i];
        const id = Number(item.id.split('/').at(-1));

        if (this.#productIdMap[id]) {
          productUrl = this.#productIdMap[id].url;
          productName = this.#productIdMap[id].name;
          break;
        }
      }
    }

    if (this.#link) {
      this.#link.classList.add('block!');
      this.#link.setAttribute('href', `${productUrl}?lb_discount=${code}&lb_discount_type=groupon`);
      this.#link.textContent = `Shop ${productName}`;
    }

    if (this.#controls) {
      this.#controls.classList.add('hidden!');
    }

    this.#setLabel(`It's good for ${discountValue} off "${productName}".`);
  }
}

if (!customElements.get('groupon-form-wrapper')) {
  customElements.define('groupon-form-wrapper', GrouponForm);
}
